import React, { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Menu, X, MapPin } from "lucide-react";

const sections = ["home", "about", "departments", "menu", "gallery", "reviews", "faq", "visit"];

export default function Navbar() {
  const { language, toggle, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F9F6F0]/85 backdrop-blur-xl border-b border-[#E2D9CD]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => handleNav("home")}
          className="flex items-center gap-3 group"
        >
          <span className="h-11 w-11 rounded-xl bg-[#F9F6F0] border border-[#E2D9CD] p-0.5 overflow-hidden shadow-sm flex items-center justify-center group-hover:scale-105 transition-transform">
            <img
              src="/images/logo.jpg"
              alt="La Hacienda Michoacana"
              className="w-full h-full object-cover rounded-lg"
            />
          </span>
          <span className="hidden sm:flex flex-col leading-tight text-left">
            <span className="font-serif-display text-[18px] tracking-tight text-[#232321]">
              La Hacienda Michoacana
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#5C5C59]">
              Supermarket · Birmingham, AL
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8 text-[14px] font-medium text-[#232321]">
          {sections.map((s) => (
            <button
              key={s}
              data-testid={`nav-${s}-link`}
              onClick={() => handleNav(s)}
              className="relative hover:text-[#C05A3A] transition-colors"
            >
              {t(`nav.${s}`)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="language-toggle"
            className="hidden sm:flex items-center text-[13px] font-medium select-none rounded-full border border-[#E2D9CD] overflow-hidden"
          >
            <button
              data-testid="lang-en-btn"
              onClick={() => language !== "en" && toggle()}
              className={`px-3 py-1.5 transition-colors ${
                language === "en" ? "bg-[#C05A3A] text-[#F9F6F0]" : "text-[#5C5C59] hover:text-[#232321]"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-es-btn"
              onClick={() => language !== "es" && toggle()}
              className={`px-3 py-1.5 transition-colors ${
                language === "es" ? "bg-[#C05A3A] text-[#F9F6F0]" : "text-[#5C5C59] hover:text-[#232321]"
              }`}
            >
              ES
            </button>
          </div>
          <a
            data-testid="navbar-directions-cta"
            href="https://www.google.com/maps/dir/?api=1&destination=La+Hacienda+Michoacan+Supermarket+118+Green+Springs+Hwy+Birmingham+AL"
            target="_blank"
            rel="noreferrer"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#232321] text-[#F9F6F0] px-4 py-2 text-[13px] font-medium hover:bg-[#C05A3A] transition-colors"
          >
            <MapPin className="h-4 w-4" strokeWidth={1.5} />
            {t("nav.cta")}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-[#232321]"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#F9F6F0] border-t border-[#E2D9CD] px-6 py-6 space-y-4"
        >
          {sections.map((s) => (
            <button
              key={s}
              data-testid={`mobile-nav-${s}`}
              onClick={() => handleNav(s)}
              className="block w-full text-left font-serif-display text-2xl text-[#232321] hover:text-[#C05A3A]"
            >
              {t(`nav.${s}`)}
            </button>
          ))}
          <div className="pt-4 flex items-center gap-3 text-[13px]">
            <button
              data-testid="mobile-lang-en"
              onClick={() => language !== "en" && toggle()}
              className={`px-4 py-2 rounded-full border ${
                language === "en"
                  ? "bg-[#C05A3A] text-[#F9F6F0] border-[#C05A3A]"
                  : "border-[#E2D9CD] text-[#5C5C59]"
              }`}
            >
              English
            </button>
            <button
              data-testid="mobile-lang-es"
              onClick={() => language !== "es" && toggle()}
              className={`px-4 py-2 rounded-full border ${
                language === "es"
                  ? "bg-[#C05A3A] text-[#F9F6F0] border-[#C05A3A]"
                  : "border-[#E2D9CD] text-[#5C5C59]"
              }`}
            >
              Español
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
